import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce, set } from 'lodash-es';
import { GlobalSettingsApi } from '../api';
import { NastaveniStatus, NastaveniUpdateVO, NastaveniVO } from '../api/gen';
import { useAuth } from '@mc/react-auth';

/**
 * Převede datum a čas na string reprezentaci pro poslání na JAVA server - formát UTC.
 * @param date date
 * @return string reprezentace pro JAVA
 */
export function getJavaDateTime(date: Date): string {
  const localDate = new Date(+date);
  localDate.setMinutes(localDate.getMinutes() + -localDate.getTimezoneOffset());
  return localDate.toISOString().split('.')[0];
}

export type GlobalSettingsProps = {
  data?: object;
  setNastaveni: (key: string, value: object) => void;
  initByProvider: boolean;
};

export const GlobalSettingsContext = React.createContext<GlobalSettingsProps>({
  data: {},
  setNastaveni: () => {},
  initByProvider: false,
});

const deepReplaceUndefined = (obj: any) => {
  let value;
  Object.keys(obj).forEach(key => {
    value = obj[key];
    if (value && typeof value === 'object') {
      deepReplaceUndefined(value);
    } else {
      if (value === '»undefined»' || value === '▓undefined▓') {
        obj[key] = undefined;
      }
    }
  });
};

const objToString = (obj: any): string => {
  return JSON.stringify(obj, (_, value) => {
    if (value === undefined) {
      return '»undefined»';
    }
    return value;
  });
};

const stringToObj = (str: string): any => {
  let obj = JSON.parse(str);
  deepReplaceUndefined(obj);
  return obj;
};

export enum GlobalSettingsProviderType {
  SERVER = 'SERVER',
  MEMORY = 'MEMORY',
  LOCAL_STORAGE = 'LOCAL_STORAGE',
}

type GlobalSettingsProviderProps = React.PropsWithChildren<{
  renderCustomLoading?: () => React.ReactNode;
  type?: GlobalSettingsProviderType;
}>;

type Item = {
  key: string;
  value: object;
};

export const GlobalSettingsProvider: React.FC<GlobalSettingsProviderProps> = ({
  renderCustomLoading,
  children,
  type = GlobalSettingsProviderType.SERVER,
}) => {
  const auth = useAuth();
  const [loading, setLoading] = useState(true);

  // Reálná data nastavení - jako objekt
  const [dataObj, setDataObj] = useState<any | undefined>();
  const queueRef = useRef<Item[]>([]);
  // Objekt nastavení s daty ve formě stringu a času poslední aktualizace
  const nastaveniRef = useRef<NastaveniVO | undefined>();
  const invalidNastaveniRef = useRef<boolean>(false);

  const pracovnikId = useMemo<number | undefined>(
    () =>
      auth?.data?.pracovnikId === undefined || auth?.data?.pracovnikId === null
        ? undefined
        : auth?.data?.pracovnikId,
    [auth?.data?.pracovnikId]
  );
  const pracovnikRef = useRef<number | undefined>(pracovnikId);
  pracovnikRef.current = pracovnikId;

  /**
   * Uložení změn nastavení na server - přes debounce.
   */
  const saveSettingsToServer = React.useCallback(
    debounce(async () => {
      const pracovnikId = pracovnikRef.current;

      if (pracovnikId !== undefined && nastaveniRef.current) {
        let queueLength = queueRef.current.length;
        let nastaveniForSave = { ...nastaveniRef.current };

        let maxAttempt = 5;
        let loop = true;
        while (maxAttempt > 0 && loop) {
          try {
            let result: NastaveniUpdateVO;

            switch (type) {
              case GlobalSettingsProviderType.SERVER:
                result = (
                  await GlobalSettingsApi.save(pracovnikId, nastaveniForSave)
                ).data;
                break;
              case GlobalSettingsProviderType.MEMORY:
                result = {
                  status: NastaveniStatus.OK,
                  nastaveni: nastaveniForSave,
                };
                break;
              case GlobalSettingsProviderType.LOCAL_STORAGE:
                nastaveniForSave.lastUpdated = getJavaDateTime(new Date());
                localStorage.setItem(
                  'GlobalSettings',
                  objToString(nastaveniForSave)
                );
                result = {
                  status: NastaveniStatus.OK,
                  nastaveni: nastaveniForSave,
                };
                break;
            }

            if (pracovnikRef.current !== pracovnikId) {
              // Mezitím se změnil pracovník, takže už nemáme co ukládat
              break;
            }

            switch (result.status) {
              case NastaveniStatus.REFRESH:
                // Data pro save na server - data ze serveru + změny z fronty, klidně celou, když děláme rebase
                queueLength = queueRef.current.length;
                const rebasedDataObj = stringToObj(result.nastaveni.data);
                queueRef.current.forEach(item => {
                  set(rebasedDataObj, item.key, item.value);
                });
                nastaveniForSave.data = objToString(rebasedDataObj);
                nastaveniForSave.lastUpdated = result.nastaveni.lastUpdated;

                // Zároveň do state a nastaveniRef dáme rebased data, lastUpdated zůstává z posledního save, ten se nastaví až po doběhnutí (OK)
                nastaveniRef.current.data = nastaveniForSave.data;
                setDataObj(rebasedDataObj);

                // ---
                maxAttempt--;
                break;
              case NastaveniStatus.OK:
                // Z fronty odebereme ty co jsme poslali na server a aktualizujeme lastUpdated ze serveru, data ve state neměníme, můžou už mít další změny než doběhl tento save
                queueRef.current.splice(0, queueLength);
                nastaveniRef.current.lastUpdated = result.nastaveni.lastUpdated;
                loop = false;
                break;
            }
          } catch (error) {
            console.warn('Chyba při ukládání nastavení:', error);
            break;
          }
        }

        if (maxAttempt === 0) {
          console.warn(
            'Nepodařilo se uložit nastavení na server, příliš mnoho pokusů.'
          );
        }
      }
    }, 500),
    [pracovnikId]
  );

  /**
   * Nastavení hodnoty do dat nastavení.
   * @param key klíč (cesta v nastavení)
   * @param value hodnota
   */
  const setValue = async (key: string, value: object) => {
    if (!nastaveniRef.current) {
      return;
    }

    if (pracovnikRef.current !== undefined) {
      // Přidání změny na konec fronty + promítnutí do data ve state a ref
      queueRef.current.push({ key, value });
      const newDataObj = stringToObj(nastaveniRef.current.data);
      set(newDataObj, key, value);
      nastaveniRef.current.data = objToString(newDataObj);
      setDataObj(newDataObj);

      // Uložení změn na server
      if (!invalidNastaveniRef.current) {
        saveSettingsToServer();
      } else {
        console.warn(
          'Nelze uložit nastavení, protože bylo načteno neplatné nastavení.'
        );
      }
    } else {
      console.warn('Nelze uložit nastavení, protože není nastaven pracovník.');
    }
  };

  /**
   * Inicializace nových dat nastavení - po prvním načtení pro daného pracovníka.
   * @param nastaveni
   */
  const initNewNastaveni = (nastaveni: NastaveniVO) => {
    try {
      setDataObj(stringToObj(nastaveni.data));
      nastaveniRef.current = nastaveni;
      queueRef.current = [];
    } catch (error) {
      console.error('Chyba při zpracování dat nastavení:', error);

      // Při chybě parsování data smažeme, protože se je stejně nepodaří parsovat ani v budoucnu
      setDataObj({});
      nastaveniRef.current = {
        data: '{}',
        lastUpdated: getJavaDateTime(new Date()),
      };
      queueRef.current = [];
    }
  };

  /**
   * Prvotní načtení dat nastavení pro daného pracovníka, volá se jen když se změní pracovník.
   * @param pracovnikId
   */
  const fetchData = async (pracovnikId?: number) => {
    if (pracovnikId !== undefined) {
      setLoading(true);
      try {
        let nastaveni: NastaveniVO;

        switch (type) {
          case GlobalSettingsProviderType.SERVER:
            nastaveni = (await GlobalSettingsApi.load(pracovnikId)).data;
            break;
          case GlobalSettingsProviderType.MEMORY:
            nastaveni = {
              data: '{}',
              lastUpdated: getJavaDateTime(new Date()),
            };
            break;
          case GlobalSettingsProviderType.LOCAL_STORAGE:
            const localData = localStorage.getItem('GlobalSettings');
            if (localData) {
              nastaveni = stringToObj(localData);
            } else {
              nastaveni = {
                data: '{}',
                lastUpdated: getJavaDateTime(new Date()),
              };
            }
        }

        initNewNastaveni(nastaveni);
      } catch (error) {
        console.error('Chyba načtení nastavení:', error);
        invalidNastaveniRef.current = true;
        const nastaveni = {
          data: '{}',
          lastUpdated: getJavaDateTime(new Date()),
        };
        initNewNastaveni(nastaveni);
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData(pracovnikId);
  }, [pracovnikId]);

  if (loading) {
    return (
      <>
        {renderCustomLoading
          ? renderCustomLoading()
          : 'Probíhá načítání globálních nastavení...'}
      </>
    );
  }

  return (
    <GlobalSettingsContext.Provider
      value={{
        data: dataObj,
        setNastaveni: setValue,
        initByProvider: true,
      }}
    >
      {children}
    </GlobalSettingsContext.Provider>
  );
};
